/**
 * ProfileChangeConfirmModal — BACKLOG-1675 (Bill 2026-10-02).
 *
 * The confirm step between "Save" on an existing customer's Person Dialog and
 * the Core Director writes. Bill: "a confirm step that shows the diff and the
 * affected accounts with a checkbox per account before anything is written,
 * defaulting to all accounts … the accounts that appear will depend on the
 * Owner Slot to be updated … and the data that is actually changed."
 *
 * Everything shown comes from the server preview (live Core Director compare +
 * per-account plan). This component collects: verification method, who
 * authorized the change ("Authorized by (full name)", CHG-0186 — when the method
 * needs it), and which accounts stay checked.
 *
 * Source citations: field numbers in the per-account rows are the DDA / SAV
 * All Fields exports (Bill 2026-10-02); verification methods are the legacy
 * Change of Contact request list minus the discontinued Security Question.
 */
import { useEffect, useMemo, useState } from 'react';
import { Banner, Button, ModalShell, Pill } from '@/components/primitives';
import type {
  ProfileChangeAccountPlan,
  ProfileChangePreviewResponse,
} from '@retail-ops/shared-types';
import { AuthorizedByField } from './AuthorizedByField';
import { authorizedByOk, normalizeAuthorizedBy } from './authorizedBy';
import './ProfileChangeConfirmModal.css';

export interface ProfileChangeConfirmParams {
  authMethodCode: string;
  /** "Authorized by": the full name of the customer who authorized the change (CHG-0186). Wire name kept. */
  customerInitials: string;
  accounts: ReadonlyArray<{ acctId: string; acctType: string }>;
}

export interface ProfileChangeConfirmModalProps {
  open: boolean;
  cifId: string;
  customerName?: string;
  preview: ProfileChangePreviewResponse;
  /** cart: the edits also live on an application; cif: a standalone CIF edit. */
  context: 'cart' | 'cif';
  applying: boolean;
  applyError?: string | null;
  /** Shown above the diff when the preview was refreshed after a stale-token refusal. */
  refreshedNotice?: string | null;
  /** CHG-0186: why this dialog opened when the officer did not edit the customer (found before signing). */
  intro?: { title: string; detail: string } | null;
  /**
   * CHG-0186 (Bill 2026-11-04): "Authorized by" starts with this name — the first-named customer on an
   * officer-created in-branch application (authorizedByPrefill). Blank everywhere else.
   */
  authorizedByPrefill?: string;
  onCancel: () => void;
  onConfirm: (params: ProfileChangeConfirmParams) => void | Promise<void>;
}

function ownerLabel(a: ProfileChangeAccountPlan): string {
  if (a.ownerSlot) return `Owner ${a.ownerSlot}`;
  return 'Owner position unknown';
}

function accountKey(a: { acctId: string; acctType: string }): string {
  return `${a.acctId}|${a.acctType}`;
}

export function ProfileChangeConfirmModal(props: ProfileChangeConfirmModalProps): JSX.Element | null {
  const {
    open, cifId, customerName, preview, context, applying, applyError, refreshedNotice, intro, authorizedByPrefill = '', onCancel, onConfirm,
  } = props;

  const methods = preview.authMethods;
  const [methodCode, setMethodCode] = useState<string>(methods[0]?.code ?? '');
  const [authorizedBy, setAuthorizedBy] = useState<string>(authorizedByPrefill);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  // Re-seed when a new preview arrives (open, or refreshed after a stale token).
  useEffect(() => {
    if (!open) return;
    setMethodCode((prev) => (methods.some((m) => m.code === prev) ? prev : (methods[0]?.code ?? '')));
    setSelected(new Set(preview.accounts.filter((a) => a.eligible && a.selectedByDefault).map(accountKey)));
    setExpanded(new Set());
  }, [open, preview, methods]);
  // "Authorized by" re-seeds when the dialog opens for a customer, not when a stale preview is refreshed.
  useEffect(() => {
    if (open) setAuthorizedBy(authorizedByPrefill);
  }, [open, authorizedByPrefill]);

  const method = useMemo(() => methods.find((m) => m.code === methodCode) ?? methods[0], [methods, methodCode]);
  const needsInitials = method?.requiresInitials ?? true;
  const eligible = useMemo(() => preview.accounts.filter((a) => a.eligible), [preview.accounts]);
  const ineligible = useMemo(() => preview.accounts.filter((a) => !a.eligible), [preview.accounts]);

  const canConfirm =
    !applying
    && preview.hasChanges
    && !!method
    && authorizedByOk(authorizedBy, needsInitials);

  const toggle = (key: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const selectAll = (on: boolean): void => {
    setSelected(on ? new Set(eligible.map(accountKey)) : new Set());
  };
  const toggleExpanded = (key: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleConfirm = (): void => {
    if (!canConfirm || !method) return;
    void onConfirm({
      authMethodCode: method.code,
      customerInitials: normalizeAuthorizedBy(authorizedBy),
      accounts: eligible.filter((a) => selected.has(accountKey(a))).map((a) => ({ acctId: a.acctId, acctType: a.acctType })),
    });
  };

  if (!open) return null;

  const cancelHint = context === 'cart'
    // Bill 2026-11-04 (CHG-0186): no deferral. Discard puts Core Director's values back on the application.
    ? 'Discard writes nothing to Core Director and puts its current values back on this application.'
    : 'Cancel writes nothing to Core Director.';

  return (
    <ModalShell
      open={open}
      onClose={applying ? () => { /* hold */ } : onCancel}
      title="Confirm changes to the customer record"
      size="lg"
      disableBackdropClose
      footer={
        <>
          <div className="pcm-footer-hint">{cancelHint}</div>
          <Button variant="ghost" onClick={onCancel} disabled={applying}>
            {context === 'cart' ? 'Discard change' : 'Cancel'}
          </Button>
          <Button variant="green" onClick={handleConfirm} disabled={!canConfirm}>
            {applying ? 'Writing to Core Director…' : 'Confirm & update Core Director'}
          </Button>
        </>
      }
    >
      <div className="pcm-meta">
        <Pill variant="info">CIF {cifId}</Pill>
        {customerName && <span className="pcm-meta__name">{customerName}</span>}
        <span className="pcm-meta__hint">Compared with Core Director just now</span>
      </div>

      {intro && <Banner tone="warn" title={intro.title}>{intro.detail}</Banner>}
      {refreshedNotice && <Banner tone="warn">{refreshedNotice}</Banner>}
      {applyError && <Banner tone="err" title="Not saved">{applyError}</Banner>}
      {preview.warnings.map((w) => (
        <Banner key={w} tone="warn">{w}</Banner>
      ))}

      {/* 1. What changed on the customer record */}
      <section className="pcm-section" aria-label="Changed fields">
        <h3 className="pcm-h">Customer record — what will change</h3>
        <table className="pcm-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Core Director has</th>
              <th>Will become</th>
            </tr>
          </thead>
          <tbody>
            {preview.diff.map((row, i) => (
              <tr key={row.field} className={i % 2 ? 'is-alt' : ''}>
                <td className="pcm-field">
                  {row.label}
                  <span className="pcm-core">{row.coreField}</span>
                </td>
                <td className="pcm-from">{row.from || <em>(blank)</em>}</td>
                <td className="pcm-to">{row.to || <em>(blank)</em>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* 2. Verification */}
      <section className="pcm-section pcm-verify" aria-label="Verification">
        <div>
          <label className="pcm-label" htmlFor="pcm-method">How was the customer verified?</label>
          <select
            id="pcm-method"
            className="pcm-input"
            value={method?.code ?? ''}
            onChange={(e) => setMethodCode(e.currentTarget.value)}
            disabled={applying}
          >
            {methods.map((m) => (
              <option key={m.code} value={m.code}>{m.label}</option>
            ))}
          </select>
        </div>
        <AuthorizedByField
          id="pcm-authorized-by"
          labelClassName="pcm-label"
          inputClassName="pcm-input"
          value={authorizedBy}
          onChange={setAuthorizedBy}
          required={needsInitials}
          prefill={authorizedByPrefill}
          disabled={applying}
        />
      </section>

      {/* 3. Accounts */}
      <section className="pcm-section" aria-label="Accounts">
        <div className="pcm-accounts-head">
          <h3 className="pcm-h">
            Apply to accounts
            {eligible.length > 0 && (
              <span className="pcm-count"> · {eligible.filter((a) => selected.has(accountKey(a))).length} of {eligible.length} selected</span>
            )}
          </h3>
          {eligible.length > 1 && (
            <div className="pcm-accounts-actions">
              <Button variant="link" size="sm" onClick={() => selectAll(true)} disabled={applying}>Select all</Button>
              <Button variant="link" size="sm" onClick={() => selectAll(false)} disabled={applying}>Clear</Button>
            </div>
          )}
        </div>

        {eligible.length === 0 && ineligible.length === 0 && (
          <p className="pcm-muted">No account carries the changed data for this customer, so only the customer record is updated.</p>
        )}

        {eligible.length > 0 && (
          <ul className="pcm-acct-list">
            {eligible.map((a) => {
              const key = accountKey(a);
              const writes = a.fields.filter((f) => f.write);
              const held = a.fields.filter((f) => !f.write);
              const isOpen = expanded.has(key);
              return (
                <li key={key} className={`pcm-acct ${selected.has(key) ? 'is-on' : ''}`}>
                  <label className="pcm-acct-row">
                    <input
                      type="checkbox"
                      checked={selected.has(key)}
                      onChange={() => toggle(key)}
                      disabled={applying}
                    />
                    <code className="pcm-acct-id">{a.acctId}</code>
                    <span className="pcm-acct-type">{a.typeLabel}</span>
                    <span className="pcm-acct-desc">{a.prodDesc ?? ''}</span>
                    <span className="pcm-acct-owner">{ownerLabel(a)}</span>
                  </label>
                  <div className="pcm-acct-summary">
                    <span>{writes.map((f) => f.label).join(', ')}</span>
                    <button type="button" className="pcm-linkbtn" onClick={() => toggleExpanded(key)}>
                      {isOpen ? 'Hide fields' : `Show fields (${writes.length}${held.length ? ` + ${held.length} held` : ''})`}
                    </button>
                  </div>
                  {isOpen && (
                    <table className="pcm-table pcm-table--inner">
                      <tbody>
                        {writes.map((f) => (
                          <tr key={f.field}>
                            <td className="pcm-field">{f.label}<span className="pcm-core">F{f.field}</span></td>
                            <td className="pcm-from">{f.from || <em>(blank)</em>}</td>
                            <td className="pcm-to">{f.to}</td>
                          </tr>
                        ))}
                        {held.map((f) => (
                          <tr key={`${f.field}-held`} className="is-held">
                            <td className="pcm-field">{f.label}<span className="pcm-core">F{f.field}</span></td>
                            <td colSpan={2} className="pcm-held">{f.reason ?? 'Not written on this account.'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {ineligible.length > 0 && (
          <div className="pcm-ineligible">
            <div className="pcm-label">Not updated here</div>
            <ul className="pcm-acct-list pcm-acct-list--muted">
              {ineligible.map((a) => (
                <li key={accountKey(a)} className="pcm-acct is-off">
                  <div className="pcm-acct-row">
                    <span className="pcm-acct-spacer" />
                    <code className="pcm-acct-id">{a.acctId}</code>
                    <span className="pcm-acct-type">{a.typeLabel}</span>
                    <span className="pcm-acct-desc">{a.prodDesc ?? ''}</span>
                    <span className="pcm-acct-owner">{ownerLabel(a)}</span>
                  </div>
                  <div className="pcm-held">{a.reason}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* 4. Note */}
      <section className="pcm-section" aria-label="Note">
        <div className="pcm-label">Note placed on the customer record, naming the accounts updated (expires per admin setting)</div>
        {/* The server previews the note with {method} / {operator} placeholders; fill what is known here. */}
        <div className="pcm-note">{preview.notePreview
          .replace('{method}', method ? `${method.label}${normalizeAuthorizedBy(authorizedBy) ? ` (${normalizeAuthorizedBy(authorizedBy)})` : ''}` : 'how the customer was verified')
          .replace('{operator}', 'your operator number')}</div>
      </section>
    </ModalShell>
  );
}
